import { useState, useMemo, useCallback } from 'react'
import { X, Plus, Trash2, Calculator, ChevronDown, ChevronUp } from 'lucide-react'
import { fmt } from '../utils/fmt'

// ── Tabela de preços MDF ──────────────────────────────────────────────────────
const MDF_PRECO = {
  branco: { '6': 240, '15': 300, '25': 500 },
  cor:    { '6': 350, '15': 450, '25': 650 },
}
const CHAPA_M2 = 5.04

const MDF_ESPESSURAS = ['6', '15', '25']
const MDF_CORES      = [
  { value: 'branco', label: 'Branco' },
  { value: 'cor',    label: 'Colorido' },
]

// ── Ferragens ─────────────────────────────────────────────────────────────────
const FERRAGENS = [
  { key: 'corredicao', label: 'Corrediça',                       preco: 20.00, unit: 'un' },
  { key: 'dobradica',  label: 'Dobradiça',                       preco:  3.00, unit: 'un' },
  { key: 'pistao',     label: 'Pistão',                          preco:  9.00, unit: 'un' },
  { key: 'fechoToque', label: 'Fecho-toque',                     preco:  7.00, unit: 'un' },
  { key: 'cabideiro',  label: 'Cabideiro',                       preco: 30.00, unit: 'un' },
  { key: 'sapatinha',  label: 'Sapatinha',                       preco:  1.00, unit: 'un' },
  { key: 'cadeirinha', label: 'Suporte prateleira',              preco:  0.50, unit: 'un' },
  { key: 'fitaBorda',  label: 'Fita de borda',                   preco:  0.56, unit: 'm'  },
  { key: 'rodinha',    label: 'Rodinha',                         preco: 12.00, unit: 'un' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtN = (v, d = 2) => Number(v || 0).toFixed(d)
const pecaVazia = () => ({ id: Date.now() + Math.random(), larg: '', alt: '', qtd: 1, esp: '15', cor: 'branco' })

// ── Stepper ───────────────────────────────────────────────────────────────────
function Stepper({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))}
        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold leading-none flex items-center justify-center">−</button>
      <span className="w-7 text-center text-sm font-bold text-slate-800">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold leading-none flex items-center justify-center">+</button>
    </div>
  )
}

// ── Componente principal ──────────────────────────────────────────────────────
export function OrcamentoPersonalizado({ open, onClose }) {
  // Peças
  const [pecas, setPecas] = useState(() => [pecaVazia(), pecaVazia(), pecaVazia()])

  // Ferragens
  const [ferragensQtd, setFerragensQtd] = useState(
    () => Object.fromEntries(FERRAGENS.map(f => [f.key, 0]))
  )

  // Precificação
  const [horas,   setHoras]   = useState('')
  const [imposto, setImposto] = useState('8')
  const [margem,  setMargem]  = useState('30')

  // Seções expansíveis (sempre abertas por padrão)
  const [secMdf,   setSecMdf]   = useState(true)
  const [secFerr,  setSecFerr]  = useState(true)
  const [secPreco, setSecPreco] = useState(true)

  // ── Atualizar peça ──────────────────────────────────────────────────────────
  const updatePeca = useCallback((id, campo, valor) => {
    setPecas(ps => ps.map(p => p.id === id ? { ...p, [campo]: valor } : p))
  }, [])

  const addPeca = useCallback(() => {
    setPecas(ps => ps.length < 20 ? [...ps, pecaVazia()] : ps)
  }, [])

  const removePeca = useCallback((id) => {
    setPecas(ps => ps.length > 1 ? ps.filter(p => p.id !== id) : ps)
  }, [])

  const updateFerragem = useCallback((key, val) => {
    setFerragensQtd(prev => ({ ...prev, [key]: val }))
  }, [])

  // ── Cálculos ────────────────────────────────────────────────────────────────
  const calculo = useMemo(() => {
    // 1. Agrupa peças por (espessura, cor) e soma m² × quantidade
    const grupos = {}
    pecas.forEach(p => {
      const l = parseFloat(p.larg) || 0
      const a = parseFloat(p.alt)  || 0
      const q = Math.max(1, parseInt(p.qtd) || 1)
      if (l <= 0 || a <= 0) return
      const m2  = (l / 100) * (a / 100) * q
      const key = `${p.esp}_${p.cor}`
      grupos[key] = (grupos[key] || 0) + m2
    })

    // 2. Custo MDF
    let custoMdf = 0
    const detalhesMdf = Object.entries(grupos).map(([key, m2]) => {
      const [esp, cor] = key.split('_')
      const precChapa  = MDF_PRECO[cor]?.[esp] ?? 0
      const custo      = (m2 / CHAPA_M2) * precChapa
      custoMdf += custo
      return { esp, cor, m2, precChapa, custo }
    })

    // 3. Custo ferragens
    let custoFerr = 0
    const detalhesFerr = FERRAGENS.map(f => {
      const qty   = ferragensQtd[f.key] || 0
      const custo = qty * f.preco
      custoFerr  += custo
      return { ...f, qty, custo }
    }).filter(f => f.qty > 0)

    // 4. Totais
    const custoMaterial  = custoMdf + custoFerr
    const horasN         = parseFloat(horas)   || 0
    const custoMaoObra   = horasN * 30
    const custoMerc      = custoMaterial + custoMaoObra

    const impostoN = parseFloat(imposto) / 100 || 0
    const margemN  = parseFloat(margem)  / 100 || 0
    const divisor  = 1 - impostoN - margemN
    const precoVenda = divisor > 0 ? custoMerc / divisor : 0

    return {
      detalhesMdf,
      detalhesFerr,
      custoMdf,
      custoFerr,
      custoMaterial,
      custoMaoObra,
      custoMerc,
      precoVenda,
      divisorValido: divisor > 0,
    }
  }, [pecas, ferragensQtd, horas, imposto, margem])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[#111111] rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2.5">
            <Calculator size={18} className="text-[#F5C200]" />
            <h2 className="text-white font-bold text-base">Orçamento Personalizado</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Conteúdo rolável */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">

          {/* ── Seção MDF ──────────────────────────────────────────────────── */}
          <section>
            <button
              type="button"
              onClick={() => setSecMdf(o => !o)}
              className="w-full flex items-center justify-between mb-3 group"
            >
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                📐 Peças de MDF
              </h3>
              {secMdf ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>

            {secMdf && (
              <>
                {/* Cabeçalho da tabela */}
                <div className="grid grid-cols-[1fr_1fr_64px_auto_auto_auto_auto] gap-2 mb-1 px-1">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Largura (cm)</span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Altura (cm)</span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase text-center">Qtd</span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Esp.</span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">Cor</span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase">m²</span>
                  <span className="w-6" />
                </div>

                {/* Linhas de peças */}
                <div className="space-y-1.5">
                  {pecas.map((p, i) => {
                    const l  = parseFloat(p.larg) || 0
                    const a  = parseFloat(p.alt)  || 0
                    const q  = Math.max(1, parseInt(p.qtd) || 1)
                    const m2 = l > 0 && a > 0 ? ((l / 100) * (a / 100) * q).toFixed(4) : '—'
                    return (
                      <div key={p.id} className="grid grid-cols-[1fr_1fr_64px_auto_auto_auto_auto] gap-2 items-center">
                        <input
                          type="number" min="0" placeholder="ex: 80"
                          value={p.larg}
                          onChange={e => updatePeca(p.id, 'larg', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                        />
                        <input
                          type="number" min="0" placeholder="ex: 60"
                          value={p.alt}
                          onChange={e => updatePeca(p.id, 'alt', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                        />
                        <input
                          type="number" min="1" step="1" placeholder="1"
                          value={p.qtd}
                          onChange={e => updatePeca(p.id, 'qtd', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 text-center font-semibold outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                        />
                        <select
                          value={p.esp}
                          onChange={e => updatePeca(p.id, 'esp', e.target.value)}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-[#F5C200] bg-white cursor-pointer"
                        >
                          {MDF_ESPESSURAS.map(e => (
                            <option key={e} value={e}>{e}mm</option>
                          ))}
                        </select>
                        <select
                          value={p.cor}
                          onChange={e => updatePeca(p.id, 'cor', e.target.value)}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-[#F5C200] bg-white cursor-pointer"
                        >
                          {MDF_CORES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                        <span className={`text-sm font-mono w-20 text-right ${m2 !== '—' ? 'text-slate-700 font-semibold' : 'text-slate-300'}`}>
                          {m2 !== '—' ? `${m2} m²` : '—'}
                        </span>
                        <button
                          type="button"
                          onClick={() => removePeca(p.id)}
                          className="w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )
                  })}
                </div>

                <button
                  type="button"
                  onClick={addPeca}
                  disabled={pecas.length >= 20}
                  className="mt-2.5 flex items-center gap-1.5 text-xs text-[#CC8800] hover:text-[#F5C200] font-semibold disabled:opacity-40"
                >
                  <Plus size={13} /> Adicionar peça
                </button>

                {/* Resumo MDF */}
                {calculo.detalhesMdf.length > 0 && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-xl space-y-1">
                    {calculo.detalhesMdf.map(d => (
                      <div key={`${d.esp}_${d.cor}`} className="flex items-center justify-between text-xs text-slate-600">
                        <span>
                          MDF {d.esp}mm {d.cor === 'branco' ? 'Branco' : 'Colorido'}
                          <span className="text-slate-400 ml-1">
                            ({fmtN(d.m2, 4)} m² ÷ {CHAPA_M2} = {fmtN(d.m2 / CHAPA_M2 * 100, 1)}% de chapa)
                          </span>
                        </span>
                        <span className="font-semibold text-slate-800">{fmt(d.custo)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs font-bold text-slate-800 border-t border-slate-200 pt-1 mt-1">
                      <span>Subtotal MDF</span>
                      <span>{fmt(calculo.custoMdf)}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── Seção Ferragens ────────────────────────────────────────────── */}
          <section>
            <button
              type="button"
              onClick={() => setSecFerr(o => !o)}
              className="w-full flex items-center justify-between mb-3 group"
            >
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                🔩 Ferragens e Acessórios
              </h3>
              {secFerr ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>

            {secFerr && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FERRAGENS.map(f => (
                  <div key={f.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 leading-snug">{f.label}</p>
                      <p className="text-[10px] text-slate-400">{fmt(f.preco)}/{f.unit}</p>
                    </div>
                    <Stepper
                      value={ferragensQtd[f.key]}
                      onChange={v => updateFerragem(f.key, v)}
                    />
                    <span className="text-xs font-semibold text-slate-600 w-14 text-right">
                      {ferragensQtd[f.key] > 0 ? fmt(ferragensQtd[f.key] * f.preco) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {calculo.custoFerr > 0 && (
              <div className="mt-2 flex justify-between text-xs font-bold text-slate-800 px-1">
                <span>Subtotal Ferragens</span>
                <span>{fmt(calculo.custoFerr)}</span>
              </div>
            )}
          </section>

          {/* ── Custo Material ─────────────────────────────────────────────── */}
          {(calculo.custoMdf + calculo.custoFerr) > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-[#FFF9C2] border border-[#F5C200]/40 rounded-xl">
              <span className="text-sm font-bold text-[#CC8800]">Custo de Material</span>
              <span className="text-base font-black text-[#111111]">{fmt(calculo.custoMaterial)}</span>
            </div>
          )}

          {/* ── Seção Precificação ─────────────────────────────────────────── */}
          <section>
            <button
              type="button"
              onClick={() => setSecPreco(o => !o)}
              className="w-full flex items-center justify-between mb-3 group"
            >
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                💰 Precificação
              </h3>
              {secPreco ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
            </button>

            {secPreco && (
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Horas Fabricação</span>
                  <div className="relative">
                    <input
                      type="number" min="0" step="0.5" placeholder="0"
                      value={horas}
                      onChange={e => setHoras(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">h</span>
                  </div>
                  {(parseFloat(horas) || 0) > 0 && (
                    <span className="text-[10px] text-slate-500">{fmt((parseFloat(horas) || 0) * 30)} mão de obra</span>
                  )}
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Imposto</span>
                  <div className="relative">
                    <input
                      type="number" min="0" max="99" step="0.1" placeholder="8"
                      value={imposto}
                      onChange={e => setImposto(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
                  </div>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Margem Contribuição</span>
                  <div className="relative">
                    <input
                      type="number" min="0" max="99" step="0.5" placeholder="30"
                      value={margem}
                      onChange={e => setMargem(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#F5C200] focus:ring-1 focus:ring-[#F5C200]/30"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
                  </div>
                </label>
              </div>
            )}
          </section>

          {/* ── Resultado Final ────────────────────────────────────────────── */}
          <div className="bg-[#111111] rounded-2xl p-5 space-y-3">
            <h3 className="text-[#F5C200] font-bold text-sm uppercase tracking-wide mb-1">Resultado</h3>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Custo Material</span>
                <span className="text-white">{fmt(calculo.custoMaterial)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Mão de obra ({fmtN(parseFloat(horas) || 0, 1)}h × R$30)</span>
                <span className="text-white">{fmt(calculo.custoMaoObra)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-300 border-t border-white/10 pt-2">
                <span>Custo da Mercadoria</span>
                <span className="text-white">{fmt(calculo.custoMerc)}</span>
              </div>
              <div className="flex justify-between text-slate-400 text-xs">
                <span>Impostos ({fmtN(parseFloat(imposto) || 0, 1)}%) + MC ({fmtN(parseFloat(margem) || 0, 1)}%)</span>
                <span>{fmtN((parseFloat(imposto) || 0) + (parseFloat(margem) || 0), 1)}% do preço</span>
              </div>
            </div>

            {calculo.divisorValido ? (
              <div className="bg-[#F5C200] rounded-xl p-4 flex items-center justify-between mt-2">
                <span className="text-[#111111] font-bold text-sm">Preço de Venda</span>
                <span className="text-[#111111] font-black text-2xl">{fmt(calculo.precoVenda)}</span>
              </div>
            ) : (
              <div className="bg-red-900/50 rounded-xl p-3 text-center mt-2">
                <p className="text-red-300 text-xs font-semibold">Imposto + Margem ≥ 100% — ajuste os percentuais</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
